// First, patch the global eval ONLY for Knockout's generic 'this' check
import './patch-eval'

// Ordering of imports important here
// ko -> css -> react
import * as ko from 'knockout';
(window as any).ko = ko;
import 'devexpress-reporting/dx-reportdesigner-imports';
import 'devexpress-reporting/dx-reportdesigner';

// Styles (Crucial for the designer to render correctly)
import '@devexpress/analytics-core/dist/css/dx-analytics.common.css';
import '@devexpress/analytics-core/dist/css/dx-analytics.light.css';
import '@devexpress/analytics-core/dist/css/dx-querybuilder.css';
import 'devexpress-reporting/dist/css/dx-reportdesigner.css';
import 'devexpress-reporting/dist/css/dx-webdocumentviewer.css';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { LoadPanel } from 'devextreme-react';
import DxReportDesigner, { RequestOptions, Callbacks } from 'devexpress-reporting-react/dx-report-designer';
import { fetchSetup } from '@devexpress/analytics-core/analytics-utils';
import { getAccessToken } from '../../services/msal';

// Global network configuration
// Modify the underlying fetch configuration right before it leaves the browser
// Attach the MSAL access token to the Report Designer's requests
fetchSetup.fetchSettings = {
    beforeSend: async (requestParameters: any) => {
        const token = await getAccessToken();
        requestParameters.headers = { ...requestParameters.headers, "Authorization": `Bearer ${token}` };
    }
}

export default function ReportDesignerForm() {
    // Grab the reportId parameter from the URL route (e.g., /report-designer/:reportId)
    const { reportId } = useParams<{ reportId?: string }>();
    const navigate = useNavigate();

    const [currentReportId, setCurrentReportId] = useState<string>(reportId || "new");

    const designerRef = useRef<any>(null);

    const [isLoading, setIsLoading] = useState(true);

    // Keep current report state synchronized if the URL parameter changes mid-session
    useEffect(() => {
        if (reportId) {
            setCurrentReportId(reportId);
        } else {
            setCurrentReportId("new");
        }
    }, [reportId]);

    // Resolve the target URL key:
    // If opening an existing report, use its GUID
    // If creating a brand new report, use a distinct placeholder string keyword  ("new")
    // instead of an empty string so the DevExpress engine registers an active editing target.
    const initialReportUrl = useMemo(() => {
        return reportId || "new";
    }, [reportId]);

    // Triggered right before a report layout changes
    const onReportOpening = ({ sender, args }: { sender: any, args: any }) => {
        // If args.Url is null, empty, or undefined, it means the user clicked "New"
        if (!args || !args.Url) {
            // Reset the URL bar back to the plain creation path
            navigate('/report-designer');
        }
    };

    const onReportOpened = ({ sender, args }: { sender: any, args: any }) => {
        setIsLoading(false);
        if (args && args.Url) {
            const openedReportId = args.Url;
            //  update the React routing path so it matches the newly loaded document
            navigate(`/report-designer/${openedReportId}`);
        }
    };

    const onReportSaved = ({ sender, args }: { sender: any, args: any }) => {
        if (args && args.Url) {
            const savedReportId = args.Url;
            // Update the URL string silently so the route changes to the new persistence ID
            navigate(`/report-designer/${savedReportId}`, { replace: true });
        }
    };

    // Intercept the Save As callback to update state and update the browser URL bar
    // const handleSave = (event: any) => {
    //     if (event && event.args && event.args.Url) {
    //         const savedUrlId = event.args.Url;
    //         setCurrentReportId(savedUrlId);
    //         navigate(`/report-designer/${savedUrlId}`, { replace: true });
    //     }
    // };

    const requestOptions = {
        // Force the host to remain at the domain root (prevents doubling)
        host: window.location.origin + "/",
        // Explicitly map the subfolder to the action itself
        getDesignerModelAction: `DXXRD/GetDesignerModel`.replace(/\/+/g, '/')
        // getDesignerModelAction: "DXXRD/GetDesignerModel"
    };

    return (
        <div className="report-designer-container">

            {isLoading && <LoadPanel visible={isLoading} />}

            <DxReportDesigner
                reportUrl={currentReportId}
                ref={designerRef}
                height="100%"
                developmentMode={true}>
                <RequestOptions
                    host={requestOptions.host}
                    getDesignerModelAction={requestOptions.getDesignerModelAction}
                />
                <Callbacks
                    ReportOpening={onReportOpening}
                    ReportOpened={onReportOpened}
                    ReportSaved={onReportSaved}
                />
            </DxReportDesigner>

        </div>
    );
}
